import React, { useState } from 'react';
import { Button, Input, Table, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { F1131, F1132, F1133 } from '../../../services/p1113Api';

// ── Empty row factories ──
const emptyF1131 = (): F1131 => ({ f1131_01: '', f1131_02: '', f1131_03: '', f1131_04: '' });
const emptyF1132 = (): F1132 => ({ f1132_01: '', f1132_02: '', f1132_03: 0, f1132_11: '' });
const emptyF1133 = (): F1133 => ({ f1133_01: '', f1133_02: '', f1133_03: 0, f1133_11: '' });

interface Props {
  isEditing: boolean;
  customerCodes: F1131[];
  compositions: F1132[];
  specClasses: F1133[];
  onCustomerCodesChange: (data: F1131[]) => void;
  onCompositionsChange: (data: F1132[]) => void;
  onSpecClassesChange: (data: F1133[]) => void;
}

const Tab2CustomerCodes: React.FC<Props> = React.memo(({
  isEditing,
  customerCodes, compositions, specClasses,
  onCustomerCodesChange, onCompositionsChange, onSpecClassesChange,
}) => {
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [specModalOpen, setSpecModalOpen] = useState(false);

  // ── F1131 Customer Codes table ──
  const handleAddRow = () => onCustomerCodesChange([...customerCodes, emptyF1131()]);
  const handleRemoveRow = (idx: number) => {
    const next = customerCodes.filter((_, i) => i !== idx);
    onCustomerCodesChange(next);
  };
  const handleChangeRow = (idx: number, field: keyof F1131, value: string) => {
    const next = [...customerCodes];
    next[idx] = { ...next[idx], [field]: value };
    onCustomerCodesChange(next);
  };

  const columns = [
    {
      title: '序號', width: 60, key: 'seq',
      render: (_: unknown, __: unknown, idx: number) => idx + 1,
    },
    {
      title: '客戶代號', dataIndex: 'f1131_03', width: 150, key: 'f1131_03',
      render: (val: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input value={val} maxLength={12} onChange={e => handleChangeRow(idx, 'f1131_03', e.target.value)} />
        ) : val,
    },
    {
      title: '客戶產品編號', dataIndex: 'f1131_04', key: 'f1131_04',
      render: (val: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input value={val} maxLength={30} onChange={e => handleChangeRow(idx, 'f1131_04', e.target.value)} />
        ) : val,
    },
    ...(isEditing ? [{
      title: '操作', width: 60, key: 'action',
      render: (_: unknown, __: unknown, idx: number) => (
        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemoveRow(idx)} />
      ),
    }] : []),
  ];

  // ── F1132 Compositions Modal ──
  const handleAddComp = () => onCompositionsChange([...compositions, emptyF1132()]);
  const handleRemoveComp = (idx: number) => onCompositionsChange(compositions.filter((_, i) => i !== idx));
  const handleChangeComp = (idx: number, val: string) => {
    const next = [...compositions];
    next[idx] = { ...next[idx], f1132_11: val };
    onCompositionsChange(next);
  };

  // ── F1133 Spec Classes Modal ──
  const handleAddSpec = () => onSpecClassesChange([...specClasses, emptyF1133()]);
  const handleRemoveSpec = (idx: number) => onSpecClassesChange(specClasses.filter((_, i) => i !== idx));
  const handleChangeSpec = (idx: number, val: string) => {
    const next = [...specClasses];
    next[idx] = { ...next[idx], f1133_11: val };
    onSpecClassesChange(next);
  };

  return (
    <div>
      {/* F1131 Customer Codes Table */}
      <div className="p1113-section-header">
        <h4>客戶產品編號 ({customerCodes.length})</h4>
        {isEditing && (
          <Button icon={<PlusOutlined />} onClick={handleAddRow} size="small">新增行</Button>
        )}
      </div>
      <Table
        dataSource={customerCodes}
        columns={columns}
        rowKey={(_, idx) => `cc-${idx}`}
        pagination={false}
        size="small"
        bordered
        scroll={{ y: 240 }}
      />

      {/* Buttons for sub-modals */}
      <div className="p1113-sub-buttons">
        <Button onClick={() => setCompModalOpen(true)}>
          用途 ({compositions.length})
        </Button>
        <Button onClick={() => setSpecModalOpen(true)}>
          規格分類 ({specClasses.length})
        </Button>
      </div>

      {/* F1132 Compositions Modal */}
      <Modal
        title="用途(材料成分明細)"
        open={compModalOpen}
        onCancel={() => setCompModalOpen(false)}
        onOk={() => setCompModalOpen(false)}
        width={500}
        destroyOnClose
      >
        {isEditing && (
          <Button icon={<PlusOutlined />} onClick={handleAddComp} size="small" className="mb-2">新增行</Button>
        )}
        <Table
          dataSource={compositions}
          rowKey={(_, idx) => `comp-${idx}`}
          pagination={false}
          size="small"
          bordered
          columns={[
            { title: '項次', width: 60, key: 'seq', render: (_: unknown, __: unknown, idx: number) => idx + 1 },
            {
              title: '成分說明', dataIndex: 'f1132_11', key: 'f1132_11',
              render: (val: string, _: unknown, idx: number) =>
                isEditing ? (
                  <Input value={val} maxLength={2} onChange={e => handleChangeComp(idx, e.target.value)} />
                ) : val,
            },
            ...(isEditing ? [{
              title: '操作', width: 60, key: 'action',
              render: (_: unknown, __: unknown, idx: number) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemoveComp(idx)} />
              ),
            }] : []),
          ]}
        />
      </Modal>

      {/* F1133 Spec Classes Modal */}
      <Modal
        title="規格分類"
        open={specModalOpen}
        onCancel={() => setSpecModalOpen(false)}
        onOk={() => setSpecModalOpen(false)}
        width={500}
        destroyOnClose
      >
        {isEditing && (
          <Button icon={<PlusOutlined />} onClick={handleAddSpec} size="small" className="mb-2">新增行</Button>
        )}
        <Table
          dataSource={specClasses}
          rowKey={(_, idx) => `spec-${idx}`}
          pagination={false}
          size="small"
          bordered
          columns={[
            { title: '項次', width: 60, key: 'seq', render: (_: unknown, __: unknown, idx: number) => idx + 1 },
            {
              title: '規格說明', dataIndex: 'f1133_11', key: 'f1133_11',
              render: (val: string, _: unknown, idx: number) =>
                isEditing ? (
                  <Input value={val} maxLength={4} onChange={e => handleChangeSpec(idx, e.target.value)} />
                ) : val,
            },
            ...(isEditing ? [{
              title: '操作', width: 60, key: 'action',
              render: (_: unknown, __: unknown, idx: number) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRemoveSpec(idx)} />
              ),
            }] : []),
          ]}
        />
      </Modal>
    </div>
  );
});

Tab2CustomerCodes.displayName = 'Tab2CustomerCodes';
export default Tab2CustomerCodes;
